/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IGroup, IGroups, IList, IOrderHistoryViewProps, IOrderInformation, ISalesOrder } from '@msdyn365-commerce-modules/order-management/src/modules/order-history/../../';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

export const OrderHistoryOrderInfomation: React.FC<IOrderInformation> = ({
    orderInformationProps,
    salesId,
    createdDate,
    amount
}) => (
        <Node {...orderInformationProps}>
            <div className='ms-order-history__order-information-sales-id-container'>
                <div className='ms-order-history__order-information-label'>Order#</div>
                <div className='ms-order-history__order-information-sales-id-wrapper'>{salesId}</div>
            </div>
            <div className='ms-order-history__order-information-created-date-container'>
                <div className='ms-order-history__order-information-label'>Date:</div>
                {createdDate}
            </div>
            <div className='ms-order-history__order-information-amount-container'>
                <div className='ms-order-history__order-information-label'>Total:</div>
                {amount}
            </div>
        </Node>
    );

export const OrderHistoryGroupDelivery: React.FC<IGroup> = ({ delivery, salesLines }) => {
    const status = salesLines[0].salesStatus;

    return (
        <Node {...delivery.deliveryProps}>
            <div className='ms-order-history__order-information-status-container'>
                <div className='ms-order-history__order-information-label'>Status:</div>
                <span>{status}</span>
            </div>
        </Node>
    );
};

export const OrderHistoryGroup: React.FC<IGroup> = (group) => (
    <Node {...group.groupProps}>
        {group.delivery && <OrderHistoryGroupDelivery {...group} />}
    </Node>
);

export const OrderHistoryGroups: React.FC<IGroups> = ({ groupsProps, groups }) => (
    <Node {...groupsProps}>
        {groups.map((group: any, index: any) => (
            <OrderHistoryGroup key={index} {...group} />
        ))}
    </Node>
);

export const OrderHistorySalesOder: React.FC<ISalesOrder> = ({ salesOrderProps, orderInfomation, groups, orderDetailsLink }) => (
    <Node {...salesOrderProps}>
        {orderInfomation && <OrderHistoryOrderInfomation {...orderInfomation} />}
        {groups && <OrderHistoryGroups {...groups} />}
        {orderDetailsLink}
    </Node>
);

export const OrderHistoryList: React.FC<IList> = ({ listProps, salesOrders }) => (
    <Node {...listProps}>{salesOrders && salesOrders.map((salesOrder, index) => <OrderHistorySalesOder key={index} {...salesOrder} />)}</Node>
);

export const OrderHistoryLabels: React.FC = () => (
    <div className='ms-order-history__label'>
        <div className='ms-order-history__label-order'>
            Order#
        </div>
        <div className='ms-order-history__label-date'>
            Date
        </div>
        <div className='ms-order-history__label-total'>
            Total
        </div>
        <div className='ms-order-history__label-status'>
            Status
        </div>
    </div>
);

const OrderHistoryView: React.FC<IOrderHistoryViewProps> = ({
    orderHistoryProps,
    alert,
    loading,
    emptyMessage,
    backToShoppingLink,
    list,
    moreButton
}) => (
        <Module {...orderHistoryProps}>
            {loading}
            {alert && (
                <>
                    {alert}
                    {backToShoppingLink}
                </>
            )}
            {emptyMessage && (
                <>
                    {emptyMessage}
                    {backToShoppingLink}
                </>
            )}
            {list && <OrderHistoryLabels />}
            {list && <OrderHistoryList {...list} />}
            {moreButton && moreButton}
        </Module>
    );

export default OrderHistoryView;
